import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import moment from "moment-timezone";
import {
  SdfBusyIndicator, SdfLink, SdfIcon, SdfPageHeaderLayout, SdfSelectSimple, SdfSegmentedControl, SdfExpandableBox,
  SdfBadge, SdfSpotIllustration, SdfButton
} from '@synerg/react-components';
import DOMPurify from 'dompurify';
import Papa from 'papaparse';

import { useApplicationState, upsertToasts } from "../../../utilities/applicationState";

import "../apiPicker.scss";

const VERB_TO_STATUS = {
  get: "in-progress",
  post: "complete",
  put: "changed",
  delete: "failed"
};

const changeSegments = [{ label: "Project APIs", value: 0 }, { label: "Event Subscriptions", value: 1 }];
const displaySegments = [{ label: "Text", value: 0 }, { label: "Path", value: 1 }];

const getHttpMethodStatus = (httpMethod) => VERB_TO_STATUS[httpMethod.toLowerCase()];

// const testEventSubscriptions = [{
//   code: "1234",
//   description: "This is a test event subscription",
//   status: "added"
// }, {
//   code: "5678",
//   description: "This is a test event subscription",
//   status: "unchanged"
// }]

const ChangeHistory = () => {
  const { projectID, clientOOID } = useParams();
  const history = useHistory();

  const [applicationState, dispatch] = useApplicationState();
  const { isApiContext } = applicationState.apiDiscovery;

  const [isBusy, setIsBusy] = useState(true);
  const [selected, setSelected] = useState(0);
  const [itemOpen, setItemOpen] = useState([]);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [changeHistory, setChangeHistory] = useState(null);
  const [changeIndex, setChangeIndex] = useState(isApiContext ? 0 : 1);

  const fetchChangeHistory = async () => {
    if (!projectID) return;
    setIsBusy(true);
    setSelected(null);
    try {
      const { data } = await axios.get(
        `${import.meta.env.VITE_ROOT_PATH}/api/project/${projectID}/client-change-history${clientOOID ? `?clientOOID=${clientOOID}` : ''}`
      );
      setChangeHistory(data);
    } catch (err) {
      dispatch(upsertToasts({
        status: "error",
        message: "Something went wrong while fetching the change history. Please try again.",
        autoCloseAfter: 30000
      }));
    }
    setSelected(0);
    setItemOpen([]);
    setIsBusy(false);
  };

  const changeSelected = (event) => {
    setSelected(parseInt(event.detail.value));
    setItemOpen([]);
  };

  const onSegmentTabChange = (index) => {
    setItemOpen([]);
    setChangeIndex(index);
  }

  useEffect(() => { fetchChangeHistory() }, [projectID]);

  const projectName = changeHistory?.name;

  const requestList = changeHistory?.commitChangesHistory?.map((change, index) => {
    const { createdAt, requestNumber } = change;
    const formatedRequestNumber = requestNumber.toString().padStart(2, "0");
    const formattedDate = moment.tz(createdAt, "America/New_York").format("MM-DD-YYYY -- hh:mm A");
    return { label: `${formatedRequestNumber} -- ${formattedDate} (ET)`, value: `${index}` };
  }) || [];

  const { apis, eventSubscriptions, createdAt, createdBy, preAuthRequired, status } = changeHistory?.commitChangesHistory?.[selected] || {};

  // const eventSubscriptions = testEventSubscriptions;

  const itemList = (changeIndex === 0 ? apis : eventSubscriptions)?.map((item, index) => {
    const isOpen = itemOpen.includes(index);
    const clickOpen = () => setItemOpen(lastItemOpen => [...lastItemOpen, index]);
    const clickClose = () => setItemOpen(lastItemOpen => lastItemOpen.filter((i) => i !== index));
    return { ...item, isOpen, clickOpen, clickClose };
  }) || [];

  const selectedRequestNumber = changeHistory?.commitChangesHistory?.[selected]?.requestNumber;
  const formattedCreatedAt = moment.tz(createdAt, "America/New_York").format("MM/DD/YYYY");

  const crPending = preAuthRequired && status !== 'completed';
  const crStatusText = crPending ? 'Pending' : 'Approved';

  const goBack = () => {
    if (clientOOID) {
      history.push({
        pathname: `/accelerator-projects/${clientOOID}`,
        state: {
          currClientOOID: clientOOID,
          currClientStatus: 'COMPLETE', // completed projects only having the change history
        }
      })
    } else {
      history.push(`/projects/${projectID}`)
    }

  }

  const downloadChangeHistory = () => {
    const downloadArr = itemList.map((item) => {
      const { httpMethod, uri, status, code } = item;
      return {
        "HTTP Method": changeIndex === 0 ? httpMethod.toUpperCase() : 'Event',
        ...(changeIndex === 0 ? { "URI": uri } : { "Event": code }),
        "Change Type": (status === 'unchanged' ? '' : (status === "added" ? "Added" : "Removed")),
        "Changed On": (status !== 'unchanged' ? formattedCreatedAt : ''),
      };
    });
    const jsonToCSV = Papa.unparse(downloadArr);
    const blob = new Blob([jsonToCSV]);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${projectName}-ChangeHistory-${selectedRequestNumber}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  return (
    <>
      {isBusy ?
        <SdfBusyIndicator
          style={{ color: "#1a8099" }}
          size="lg"
          overlay={true}
          fullScreen={false}
        /> :
        <div className="container px-20 py-10 lg:px-12">
          <SdfLink className="cursor-pointer" slot="breadcrumb" onClick={goBack}>
            <SdfIcon icon="nav-move-left" className="hydrated" />
            {`Back to ${projectName}`}
          </SdfLink>
          <SdfPageHeaderLayout className="hydrated">
            <div slot="title-content">
              <div className="flex items-center gap-3">
                <div className="text-heading-06 font-black">{`Change History for ${projectName}`}</div>
              </div>
            </div>
          </SdfPageHeaderLayout>
          <section className="px-5 py-5 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center gap-2 pb-5">
                <div className="text-heading-02 font-bold"> Scope Approval</div> <SdfBadge className="min-w-20" status={crPending ? 'changed' : 'complete'} >{crStatusText}</SdfBadge>
              </div>
            <div className="flex gap-4 lg:gap-12">
              <div className="min-w-32">
                <div>
                  <label htmlFor="change-request" className="font-medium">Change Request</label>
                </div>
                <SdfSelectSimple
                  items={requestList}
                  value={requestList?.[selected]}
                  onSdfChange={changeSelected}
                  id="change-request"
                />
              </div>
              <div className="min-w-32">
                <div className="font-medium">Request Number</div>
                <div className="pt-2 text-heading-05 font-bold">{selectedRequestNumber?.toString().padStart(2, "0")}</div>
              </div>
              <div className="min-w-28">
                <div className="font-medium">Change Date</div>
                <div className="pt-2 text-heading-05 font-bold truncate">
                  <span>{formattedCreatedAt}</span>
                </div>
              </div>
              <div className="min-w-28">
                <div className="font-medium">Requested By</div>
                <div className="pt-2 text-heading-05 font-bold truncate">
                  <span>{createdBy?.name}</span>
                </div>
              </div>
            </div>
          </section>
          <div className="flex w-full items-center mt-5">
            <SdfSegmentedControl
              items={changeSegments}
              onSdfChange={(event) => onSegmentTabChange(event.detail)}
              selectedIndex={changeIndex}
              className="m-auto"
            />
            {clientOOID && <div className="flex justify-end mt-5">
              <SdfButton
                emphasis={"primary"}
                className="text-body-02 font-bold"
                onClick={() => downloadChangeHistory()}
              >
                Export
              </SdfButton>
            </div>}
          </div>
          <div className="flex justify-between items-center my-5">
            <h1>{`${changeIndex === 0 ? "Data Connector" : "Event Subscriptions"} (${ itemList?.length })`}</h1>
            {
              changeIndex === 0 ?
              <SdfSegmentedControl
                items={displaySegments}
                onSdfChange={(event) => setDisplayIndex(event.detail)}
                selectedIndex={displayIndex}
              />: ""
            }
          </div>
          {
            itemList.length > 0 ?
            <>
              {
                itemList.map((item, index) => {
                  const { httpMethod, uri, text, description, code, status, isOpen, clickOpen, clickClose } = item;

                  return <div key={`change-history-item-list-${changeIndex === 0 ? `${httpMethod}-${uri}-${index}` : `${code}-${index}`}`} className='h-auto'>
                    <SdfExpandableBox
                      opened={isOpen}
                      className={`mb-4${status === "added" || status === "removed" ? " bg-gray-50" : ""}`}
                      variant="shadowed"
                      triggerPlacement="after"
                      stroke="light"
                      headingLevel="3"
                      onSdfClose={clickClose}
                      onSdfOpen={clickOpen}
                    >
                      <div slot="header" className="w-full flex flex-row justify-between items-center change-history-api-header">
                        <div className="flex flex-row min-w-0 gap-4 items-start pr-4">
                          <span className="text-heading-04 font-bold w-full">
                            <div className='flex items-center'>
                              {changeIndex === 0 ?
                                <>
                                  {displayIndex ?
                                    <>
                                      <SdfBadge className='min-w-20' status={getHttpMethodStatus(httpMethod) || "default"}>
                                        {httpMethod.toUpperCase()}
                                      </SdfBadge>
                                      <div className="truncate">{uri}</div>
                                    </> :
                                    <div className="truncate">{text}</div>
                                  }
                                </> :
                                <div className="truncate">{code}</div>
                              }
                            </div>
                          </span>
                        </div>
                        <div className="min-w-36">
                          { status === "added" &&
                            <div className="text-alert-success pr-8">
                              <SdfIcon icon="alert-success" className="hydrated pr-3" />
                              Added
                            </div>
                          }
                          { status === "removed" &&
                            <div className="text-alert-error pr-8">
                              <SdfIcon icon="alert-error" className="hydrated pr-3" />
                              Removed
                            </div>
                          }
                        </div>
                      </div>
                      <div className='mt-4'>
                        <div className='text-heading-02 font-bold mt-4 mb-2'>Description</div>
                        <div className="text-body-02 mb-8" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(changeIndex === 1 ? (text || description) : description) }}></div>
                      </div>
                    </SdfExpandableBox>
                  </div>
                })
              }
            </> :
            <div className="flex flex-col items-center">
              <SdfSpotIllustration
                illustrationName={'empty-inbox'}
                size={'md'}
              />
              <div className="text-heading-04 font-bold">
                {/* TODO - get more details on the illustration and container */}
                No {changeIndex === 0 ? "APIs" : "event subscriptions"} exist
              </div>
            </div>
          }
        </div>
      }
    </>
  );
};

export default ChangeHistory;
